import { type JSONContent } from '@tiptap/core';

import {
  WORKSPACE_AGENT_PILL_NODE_NAME,
  type WorkspaceAgentPillNodeAttrs,
} from '~/components/v2/components/workspace/workspace-agent-panel/tiptap/workspaceAgentPill';
import { buildWorkspaceAgentComposerPlainTextContent } from '~/components/v2/components/workspace/workspace-agent-panel/utils/buildWorkspaceAgentComposerPlainTextContent';
import { splitPillMonikers } from '~/components/v2/components/workspace/workspace-agent-panel/utils/splitPillMonikers';

type WorkspaceAgentSeedBoardPillNode = { boardId: string; label: string; pillId: string };

type BuildWorkspaceAgentComposerSeedContentOptions = {
  boardPill?: WorkspaceAgentSeedBoardPillNode;
  prompt: string;
};

const buildBoardPillNode = ({ boardId, label, pillId }: WorkspaceAgentSeedBoardPillNode): JSONContent => {
  const attrs: WorkspaceAgentPillNodeAttrs = {
    assetId: null,
    assetType: null,
    boardId,
    contextSourceFieldValueId: null,
    contextSourceId: null,
    durableWireEntityType: null,
    ext: null,
    id: pillId,
    label,
    thumbnailUrl: null,
    type: 'board',
    versionId: null,
  };

  return { attrs, type: WORKSPACE_AGENT_PILL_NODE_NAME };
};

const toTextNode = (text: string): JSONContent => ({ text, type: 'text' });

/**
 * Seeds go through the same paragraph-per-line shape as plain prompts. A `@[board:<boardId>]`
 * moniker naming the seed's board becomes that board's pill. The composer serializer sends it
 * back as `@[board:<boardId>]`, with the client id only on the metadata pill. Any other moniker
 * stays plain text.
 */
export const buildWorkspaceAgentComposerSeedContent = ({
  boardPill,
  prompt,
}: BuildWorkspaceAgentComposerSeedContentOptions): JSONContent[] => {
  const paragraphs: JSONContent[] = buildWorkspaceAgentComposerPlainTextContent(prompt);

  if (!boardPill) {
    return paragraphs;
  }

  return paragraphs.map((paragraph) => {
    const text = paragraph.content?.[0]?.text;

    if (text === undefined) {
      return paragraph;
    }

    return {
      ...paragraph,
      content: splitPillMonikers(text).map((segment) =>
        segment.type === 'pill' && segment.entityType === 'board' && segment.monikerId === boardPill.boardId
          ? buildBoardPillNode(boardPill)
          : toTextNode(segment.type === 'text' ? segment.value : segment.raw),
      ),
    };
  });
};
